import { useEffect, useState } from 'react';
import { LogOut, Settings, Loader2, Building2, Globe, Library } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useLocation as useRouterLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/hooks/use-role';
import { LocationFilter } from '@/components/filters/LocationFilter';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import OrgSwitcher from './OrgSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { usePageTitleValue } from '@/contexts/PageTitleContext';
import { useHeaderSlotValue } from '@/contexts/HeaderSlotContext';
import { TypewriterBackButton } from './TypewriterBackButton';
import { NotificationsBell } from '@/features/notifications/NotificationsBell';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface TopHeaderProps {
  stationMode?: boolean;
  /**
   * Setup mode (BLU-1376, handover 4.5): no sidebar toggle, no header slot, a
   * PrintRun wordmark with a Setup chip, no notifications bell (its page is
   * off the setup allowlist and it polls) and no Settings menu item (Settings
   * is off the allowlist). The org switcher, language toggle and Log out stay.
   */
  setupMode?: boolean;
}

export function TopHeader({ stationMode = false, setupMode = false }: TopHeaderProps) {
  const navigate = useNavigate();
  const { pathname } = useRouterLocation();
  const { profile, signOut } = useAuth();
  const { isPlatformAdmin, can } = useRole();
  const { language, setLanguage, isChanging } = useLanguage();
  const { t } = useTranslation();
  const { title, subtitle, back } = usePageTitleValue();
  const { workspace } = useWorkspace();
  // Operational production pages must not offer inventory-only locations.
  // Settings is the intentional exception: admins need to select a currently
  // disabled location there in order to turn production back on.
  const productionLocationsOnly =
    workspace === 'production' && !pathname.startsWith('/production/settings');
  // BLU-3xx Phase 47.01.2-B-fix3.4: page-registered slot for header-left
  // content. Today: cross-org client filter on /inventory and /receive-history.
  const leftSlot = useHeaderSlotValue();

  const [orgSwitching, setOrgSwitching] = useState(false);

  useEffect(() => {
    const onStart = () => setOrgSwitching(true);
    const onComplete = () => setOrgSwitching(false);
    window.addEventListener('org-changed', onStart);
    window.addEventListener('org-change-complete', onComplete as EventListener);
    return () => {
      window.removeEventListener('org-changed', onStart);
      window.removeEventListener('org-change-complete', onComplete as EventListener);
    };
  }, []);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <header className="sticky top-0 z-50 flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4 relative">
      {!stationMode && !setupMode && (
        <>
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
        </>
      )}

      {setupMode && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{t('onboarding:setupMode.brand')}</span>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary-strong">
            {t('onboarding:setupMode.chip')}
          </span>
        </div>
      )}

      {/* BLU-3xx Phase 47.01.2-B-fix3.4: page-registered left slot. Sits
          adjacent to the sidebar toggle so cross-org context (e.g. the
          ClientFilterDropdown on /inventory) is anchored to the global
          chrome rather than scrolling with the page body. */}
      {!stationMode && !setupMode && leftSlot && (
        <div className="flex items-center">{leftSlot}</div>
      )}

      {/* Per-page back affordance — typewriter animation on hover, brand-purple
          highlight. Set via the `back` field on usePageTitle. */}
      {back && <TypewriterBackButton label={back.label} to={back.to} onClick={back.onClick} />}

      {/* Spacer pushes the right-hand controls to the edge. The centred title
          below is absolutely positioned so it stays anchored to the geometric
          centre regardless of how wide the left/right clusters become. */}
      <div className="flex-1" />

      {/* Centred page title — absolutely positioned so it doesn't shift when
          the typewriter button or any other left-side chrome grows. Uses
          `pointer-events-none` on the wrapper so it never blocks the
          underlying flex children; the title itself re-enables pointer
          events. `overflow-visible` so the EditingPill's ring renders cleanly. */}
      {title ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-14 items-center justify-center md:flex">
          <div className="pointer-events-auto flex max-w-[48ch] flex-col items-center px-4">
            <span
              data-page-title
              className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold tracking-tight text-foreground"
            >
              {title}
            </span>
            {subtitle ? (
              <span className="max-w-full truncate whitespace-nowrap text-xs text-muted-foreground">
                {subtitle}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Right side controls. min-w-0 lets the location switcher give way on a
          phone instead of pushing the avatar off the screen. */}
      <div className="flex min-w-0 items-center gap-3">
        <LocationFilter productionOnly={productionLocationsOnly} />

        {/* Notifications bell — inventory-app chrome only. Stations are
            chrome-free (PRODUCTION_DESIGN.md), so it's suppressed there. */}
        {!stationMode && !setupMode && profile && <NotificationsBell />}

        {profile && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="text-xs">
                    {getInitials(profile.first_name, profile.last_name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {profile.first_name} {profile.last_name}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {profile.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* BLU-641: NOT gated on stationMode — a multi-org station user
                  (3PL crew working two orgs) must always have an org-switch
                  path; OrgSwitcher self-collapses to a static label for
                  single-org users, so kiosks stay uncluttered. */}
              <div className="px-2 py-2">
                <div className="text-xs text-muted-foreground mb-2 flex items-center justify-between">
                  <span>{t('nav:store')}</span>
                  {orgSwitching && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Switching organization" />
                  )}
                </div>
                <OrgSwitcher />
              </div>
              {!stationMode && <DropdownMenuSeparator />}
              {!setupMode && can('org_settings.manage') && (
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>{t('nav:inventory.settings')}</span>
                </DropdownMenuItem>
              )}
              {isPlatformAdmin() && (
                <DropdownMenuItem onClick={() => navigate('/platform/stores')}>
                  <Building2 className="mr-2 h-4 w-4" />
                  <span>{t('nav:manageStores')}</span>
                </DropdownMenuItem>
              )}
              {isPlatformAdmin() && (
                <DropdownMenuItem onClick={() => navigate('/platform/catalog')}>
                  <Library className="mr-2 h-4 w-4" />
                  <span>{t('nav:supplierCatalog')}</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem disabled={isChanging} onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}>
                <Globe className="mr-2 h-4 w-4" />
                <span>{language === 'zh' ? t('common:switchToEnglish') : t('common:switchToChinese')}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => signOut()}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t('nav:logOut')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
